import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { PieDonutChartProps } from "./types";
import ChartLegend from "./ChartLegend";
import Typography from "../Typography";

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

const PieDonutChart: React.FC<PieDonutChartProps> = ({
  data,
  totalLabel = "Total",
  totalValue = "0h",
  dataKey,
  heading,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleLegendClick = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="flex flex-col gap-1 w-full p-2 bg-gray-100 rounded-xl">
      <div className="flex gap-2 items-center">
        <Typography
          variant="LG"
          text={heading}
          strong
          className="pl-5 py-2 w-fit"
        />
        {":"}
        <Typography
          variant="LG"
          text={totalValue}
          strong
          className="py-2 w-fit"
        />
      </div>
      <div className="flex flex-col lg:flex-row items-center">
        <div className="relative w-[350px] h-[350px]">
          {/* Donut Chart */}
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                innerRadius={75}
                outerRadius={150}
                activeIndex={activeIndex ?? -1}
                activeShape={renderActiveShape}
                stroke="#fff"
                startAngle={90}
                endAngle={450}
                isAnimationActive
                animationDuration={500}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={
                      activeIndex === null || activeIndex === index ? 1 : 0.4
                    }
                    style={{
                      transition: "fill-opacity 0.3s ease, transform 0.3s ease",
                      transform:
                        activeIndex === index ? "scale(1.05)" : "scale(1)",
                      transformOrigin: "center",
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Middle Circle */}
          <div className="absolute top-1/2 left-1/2 w-[190px] h-[190px] bg-white opacity-85 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-10" />

          {/* Center Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-30 pointer-events-none transition-opacity duration-300">
            <p className="text-gray-400 text-sm">
              {activeIndex !== null ? data[activeIndex].name : totalLabel}
            </p>
            <p className="text-black text-xl font-semibold">
              {activeIndex !== null
                ? dataKey === "price"
                  ? `$${data[activeIndex][dataKey]?.toLocaleString()}`
                  : `${data[activeIndex][dataKey]}h`
                : totalValue}
            </p>
          </div>
        </div>

        {/* Legend */}
        <ChartLegend
          data={data}
          activeIndex={activeIndex}
          onClick={handleLegendClick}
        />
      </div>
    </div>
  );
};

export default PieDonutChart;
